import React from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useGraphEditorStore } from '@/graph-store';
import type { ModalDescriptor } from '@/graph-store/types';
import { AddMaterialModal } from '../AddMaterialModal';
import { AddStepModal } from '../AddStepModal';
import { StepModalEdit } from '../AddStepModal/StepModalEdit';
import { EditMaterialModal } from '../EditMaterialModal';
import { DeleteConfirmationDialog } from '../DeleteConfirmationDialog';
import { AddGroupModal } from '../AddGroupModal';
import { EditGroupModal } from '../EditGroupModal';
import { AddCycleHereDialog } from '../AddCycleHereDialog';
import { TransferInsertPositionModal } from '../TransferInsertPositionModal';
import { LinkMaterialModal } from '../LinkMaterialModal';
import { MergeMaterialModal } from '../MergeMaterialModal';
import { EntityExplorerModal } from '../EntityExplorerModal';
import { useToast } from '@/hooks/useToast';
import type { Archetype } from '@/graph/domain/types';
import type { DeleteTarget } from '../DeleteConfirmationDialog';
import type { EntityExplorerConfig, EntityExplorerResult } from '@/types/entity-explorer.types';

export const ModalOutlet: React.FC = () => {
  const stack = useGraphEditorStore((s) => s.modals.stack);
  const closeModal = useGraphEditorStore((s) => s.closeModal);
  const { showSuccess, showError } = useToast();

  // Render all modals in the stack
  // MUI Dialog handles z-index stacking automatically
  return (
    <>
      {stack.map((modal, index) => (
        <ModalContent key={index} modal={modal} closeModal={closeModal} showSuccess={showSuccess} showError={showError} />
      ))}
    </>
  );
};

interface ModalContentProps {
  modal: ModalDescriptor;
  closeModal: () => void;
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
}

const ModalContent: React.FC<ModalContentProps> = ({ modal, closeModal, showSuccess, showError }) => {
  switch (modal.kind) {
    case 'add-material':
      return (
        <AddMaterialModal
          position={modal.payload?.position as { x: number; y: number } | undefined}
          onClose={closeModal}
          onSaved={(materialId) => {
            // Optional caller callback (e.g. transfer/complex "New Destination")
            // so the parent Add Step modal can select the newly created material.
            const onSavedCallback = modal.payload?.onSaved as
              | ((id: string) => void)
              | undefined;
            onSavedCallback?.(materialId);
            showSuccess('Material added successfully');
          }}
        />
      );
    case 'add-step':
      return (
        <AddStepModal
          sourceMaterialId={modal.payload?.sourceMaterialId as string}
          sourceMaterialIds={modal.payload?.sourceMaterialIds as string[] | undefined}
          archetypes={modal.payload?.archetypes as Archetype[] | undefined}
          onClose={closeModal}
          onSaved={() => showSuccess('Step added successfully')}
        />
      );
    case 'insert-step':
      return (
        <AddStepModal
          sourceMaterialId={modal.payload?.anchorMaterialId as string}
          archetypes={modal.payload?.archetypes as Archetype[] | undefined}
          mode="insert"
          onClose={closeModal}
          onSaved={() => showSuccess('Step inserted successfully')}
        />
      );
    case 'transfer-insert-position':
      return (
        <TransferInsertPositionModal
          anchorMaterialId={modal.payload?.anchorMaterialId as string}
          transferOpId={modal.payload?.transferOpId as string}
          role={modal.payload?.role as 'source' | 'destination'}
          onClose={closeModal}
        />
      );
    case 'edit-step':
      return (
        <StepModalEdit
          operationId={modal.payload?.operationId as string}
          onClose={closeModal}
        />
      );
    case 'edit-material':
      return (
        <EditMaterialModal
          materialId={modal.payload?.materialId as string}
          onClose={closeModal}
          onSaved={() => showSuccess('Material updated successfully')}
        />
      );
    case 'add-group':
      return (
        <AddGroupModal
          preselectedStepIds={modal.payload?.stepIds as string[] | undefined}
          onClose={closeModal}
        />
      );
    case 'edit-group':
      return (
        <EditGroupModal
          groupId={modal.payload?.groupId as string}
          onClose={closeModal}
        />
      );
    case 'add-cycle':
      return (
        <AddCycleHereDialog
          groupId={modal.payload?.groupId as string}
          anchorOpId={modal.payload?.anchorOpId as string | undefined}
          onClose={closeModal}
        />
      );
    case 'confirm-ungroup': {
      const ungroupId = modal.payload?.groupId as string;
      const ungroupLabel =
        useGraphEditorStore.getState().graphData.groups[ungroupId]?.label ?? 'this group';
      const handleUngroup = () => {
        useGraphEditorStore.getState().removeGroup(ungroupId);
        closeModal();
      };
      return (
        <Dialog open onClose={closeModal} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            Ungroup
            <IconButton
              aria-label="close"
              onClick={closeModal}
              sx={{
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Typography>
              Remove group &ldquo;{ungroupLabel}&rdquo;? Steps will remain in the workflow.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeModal}>Cancel</Button>
            <Button variant="contained" color="error" onClick={handleUngroup}>
              Ungroup
            </Button>
          </DialogActions>
        </Dialog>
      );
    }
    case 'confirm-delete': {
      const target = modal.payload as unknown as DeleteTarget;
      const handleConfirm = () => {
        try {
          const store = useGraphEditorStore.getState();
          if (target.kind === 'operation' && target.operationId) {
            store.deleteOperationStep(target.operationId);
            showSuccess('Step deleted successfully');
          } else if (target.kind === 'material' && target.materialIds && target.materialIds.length > 1) {
            store.deleteMaterials(target.materialIds);
            showSuccess('Materials deleted successfully');
          } else if (target.kind === 'material' && target.materialId) {
            store.deleteMaterial(target.materialId);
            showSuccess('Material deleted successfully');
          }
          closeModal();
        } catch (error) {
          const message =
            error instanceof Error ? error.message : 'Failed to delete selected item.';
          showError(message);
        }
      };
      return (
        <DeleteConfirmationDialog
          target={target}
          onConfirm={handleConfirm}
          onCancel={closeModal}
        />
      );
    }
    case 'link-material':
      return (
        <LinkMaterialModal
          sourceMaterialId={modal.payload?.sourceMaterialId as string}
          onClose={closeModal}
          onLinked={() => showSuccess('Materials linked successfully')}
        />
      );
    case 'merge-material':
      return (
        <MergeMaterialModal
          sourceMaterialId={modal.payload?.sourceMaterialId as string}
          contextType={modal.payload?.contextType as 'ROOT' | 'LEAF'}
          onClose={closeModal}
          onMerged={() => showSuccess('Materials merged successfully')}
        />
      );
    case 'entity-explorer':
      return (
        <EntityExplorerModal
          open={true}
          config={modal.payload?.config as EntityExplorerConfig}
          onClose={closeModal}
          onConfirm={(result: EntityExplorerResult) => {
            const callback = (modal.payload as any)?.onConfirm;
            if (callback) {
              callback(result);
            }
            // Auto-close entity-explorer after callback processes the result
            // This allows the parent modal to remain open while just the picker closes
            closeModal();
          }}
        />
      );
    default:
      return null;
  }
};
